// These 3 documents aren't covered by the real RelayRigs app Legal Center
// (GET /api/legal/documents) — everything else is fetched live in LegalPage.jsx.
export const LEGAL_PAGES = {
  "carrier-requester-agreement": {
    title: "Carrier / Requester Agreement",
    lastUpdated: "August 2026",
    sections: [
      {
        heading: "1. Who This Agreement Covers",
        body: "This agreement applies to any user posting an assignment on RelayRigs, including carriers, fleet managers, owner operators requesting driver assistance, and RV owners, dealers, or fleets requesting recovery or delivery.",
      },
      {
        heading: "2. Posting Assignments",
        body: "When you post an assignment, you agree to provide accurate pickup, destination, equipment, vehicle, cargo, and compensation information. Inaccurate information may result in removal of the posting or restriction of your account.",
      },
      {
        heading: "3. Reliance on Driver Verification",
        body: "RelayRigs verification workflows are designed to build trust into the marketplace but do not constitute a government certification or guarantee of a driver's qualifications, conduct, or performance. You remain responsible for any qualification requirements specific to your operation.",
      },
      {
        heading: "4. Payment & Funding Obligations",
        body: "Where an assignment requires secured driver compensation, you agree to fund the required amount prior to the driver beginning eligible paid work, in accordance with our Refund & Cancellation Policy.",
      },
      {
        heading: "5. Vehicle, Load & RV Representations",
        body: [
          "You represent that you have legal ownership or authorization for any vehicle, load, or RV listed on the Platform.",
          "For RV recovery requests, you confirm the vehicle is safely drivable if requesting driveaway service, and will disclose known mechanical issues.",
          "You will provide required documentation (registration, ownership, or agency authorization) before vehicle custody is transferred.",
        ],
      },
      {
        heading: "6. Return Transportation for Relief Drivers",
        body: "When requesting a relief driver, you are responsible for arranging and funding that driver's return transportation (flight or coach bus) back to where they were picked up, built into the assignment's compensation. RelayRigs does not book or provide this travel.",
      },
      {
        heading: "7. Insurance Requirements",
        body: "You are responsible for maintaining insurance coverage appropriate to your equipment, cargo, or RV, and for verifying that coverage extends to drivers matched through RelayRigs where required.",
      },
      {
        heading: "8. Termination",
        body: "RelayRigs may remove a posting or restrict access for inaccurate information, failure to fund compensation, unauthorized recovery requests, or other violations of this agreement.",
      },
    ],
  },

  accessibility: {
    title: "Accessibility Statement",
    lastUpdated: "August 2026",
    sections: [
      {
        heading: "1. Our Commitment",
        body: "RelayRigs is committed to making its website and application usable by as many people as possible, including drivers and requesters with disabilities.",
      },
      {
        heading: "2. Standards We Aim to Meet",
        body: "We aim to align with the Web Content Accessibility Guidelines (WCAG) 2.1, Level AA, as a general target for color contrast, keyboard navigation, and screen reader compatibility.",
      },
      {
        heading: "3. Ongoing Efforts",
        body: "Accessibility is an ongoing effort. As RelayRigs continues development, we periodically review and improve site components to reduce accessibility barriers.",
      },
      {
        heading: "4. Feedback",
        body: "If you encounter an accessibility barrier while using RelayRigs, please contact accessibility@relayrigs.com so we can address it.",
      },
    ],
  },

  "compliance-information": {
    title: "Compliance Information",
    lastUpdated: "August 2026",
    sections: [
      {
        heading: "1. Regulatory Status Overview",
        body: "RelayRigs is a transportation technology marketplace in development. RelayRigs does not represent itself as a government agency, a motor carrier, a freight broker, an insurance company, or a licensed escrow provider unless and until the applicable license, registration, or authorization actually exists for that specific service.",
      },
      {
        heading: "2. ELD Terminology",
        body: "RelayRigs does not advertise itself as an Electronic Logging Device (ELD) unless it becomes a registered, self-certified ELD with FMCSA. Where RelayRigs integrates with a supported ELD provider, the terminology used is \"FMCSA Registered / Self Certified ELD, where applicable\" — never \"DOT Approved ELD.\"",
      },
      {
        heading: "3. Insurance Terminology",
        body: "RelayRigs does not present itself as an insurance company. Insurance-related features are designed to verify applicable information and, where available, integrate with licensed third-party insurance partners. Separately, RelayRigs maintains its own corporate General Liability and Cyber / Technology Errors & Omissions (Tech E&O) insurance coverage for its own operations — this is distinct from, and not a substitute for, marketplace users' own required insurance coverage. See our Insurance Coverage Disclosure for details.",
      },
      {
        heading: "4. Payment & Escrow Terminology",
        body: "RelayRigs distinguishes between platform/subscription revenue and funds designated for driver compensation, and does not use the term \"escrow\" unless the final payment structure and a licensed provider support that terminology.",
      },
      {
        heading: "5. RV Recovery Authorization",
        body: "RelayRigs requires appropriate ownership or authorization information (identity, VIN, registration, ownership documentation, or agency authorization) before sensitive recovery information or vehicle custody is provided, and does not dispatch a drive-away driver when a vehicle requires towing.",
      },
      {
        heading: "6. Roadside Inspection Mode",
        body: "RelayRigs Inspection Mode is designed to help drivers organize relevant information and connect to appropriate ELD inspection workflows. It does not replace FMCSA systems or law enforcement authority.",
      },
      {
        heading: "7. Jurisdictional Availability",
        body: "RelayRigs is architected to begin operating in the United States while being designed for future international expansion. Regulatory, insurance, ELD, payment, and marketplace services depend on applicable requirements, jurisdictions, and third-party providers, and may vary or be unavailable in some regions.",
      },
      {
        heading: "8. Contact",
        body: "Compliance-related questions can be directed to compliance@relayrigs.com.",
      },
    ],
  },

  "data-processing-agreement": {
    title: "Data Processing Agreement",
    lastUpdated: "August 2026",
    sections: [
      {
        heading: "1. Purpose & Scope",
        body: "This Data Processing Agreement (\"DPA\") describes how RelayRigs (\"Processor\") handles personal data on behalf of business customers, carriers, and partners (\"Controller\") who use the Platform to post assignments, manage drivers, or otherwise submit personal data of third parties.",
      },
      {
        heading: "2. Roles & Responsibilities",
        body: [
          "Controller determines the purposes and means of processing personal data it submits to the Platform, and is responsible for having a lawful basis to share that data with RelayRigs.",
          "Processor (RelayRigs) processes that data only on Controller's documented instructions and as needed to provide the Platform, and does not sell or use it for unrelated purposes.",
        ],
      },
      {
        heading: "3. Categories of Data Processed",
        body: [
          "Identity and contact information of drivers, owner operators, or carrier personnel (name, email, phone, company, USDOT number).",
          "Assignment, location, and vehicle/RV data associated with a posted job.",
          "Payment and subscription status (no full card numbers are stored by RelayRigs; payment processing is handled by a licensed third-party processor).",
        ],
      },
      {
        heading: "4. Sub-processors",
        body: "RelayRigs may engage third-party sub-processors (for example, cloud hosting and payment processing providers) to support the Platform, and remains responsible for ensuring those sub-processors provide an equivalent level of data protection.",
      },
      {
        heading: "5. Security Measures",
        body: [
          "Encryption of data in transit (HTTPS/TLS).",
          "Access controls limiting who can view administrative data, gated behind authenticated sessions.",
          "Rate limiting and abuse monitoring on data-submission endpoints.",
        ],
      },
      {
        heading: "6. Data Breach Notification",
        body: "In the event of a confirmed personal data breach affecting Controller's data, RelayRigs will notify the affected Controller without undue delay, and in any case no later than 72 hours after becoming aware of the breach.",
      },
      {
        heading: "7. International Transfers",
        body: "Where personal data is transferred outside the United States, RelayRigs uses safeguards recognized under applicable data protection law for that transfer.",
      },
      {
        heading: "8. Term & Termination",
        body: "This DPA remains in effect for as long as RelayRigs processes personal data on Controller's behalf under the underlying Terms of Service or Master Service Agreement. Upon termination, RelayRigs will delete or return Controller's personal data, except where retention is required by law.",
      },
      {
        heading: "9. Liability",
        body: "Each party's liability under this DPA is subject to the limitation of liability set out in RelayRigs' Terms of Service.",
      },
      {
        heading: "10. Governing Law",
        body: "This DPA is governed by the laws of the State of Florida, without regard to conflict-of-laws principles.",
      },
    ],
  },

  "master-service-agreement": {
    title: "Master Service Agreement",
    lastUpdated: "August 2026",
    sections: [
      {
        heading: "1. Scope of Services",
        body: "RelayRigs provides a transportation technology marketplace connecting drivers, owner operators, carriers, and RV owners with assignments, relief loads, and recovery/delivery opportunities, along with related account, verification, and payment-status features (the \"Services\").",
      },
      {
        heading: "2. Term",
        body: "This Master Service Agreement (\"MSA\") becomes effective when you first access the Platform and continues until terminated as described below.",
      },
      {
        heading: "3. Fees & Payment",
        body: "Fees for any paid plan are as displayed on our Pricing page at the time of purchase and are billed according to our Refund & Cancellation Policy.",
      },
      {
        heading: "4. Service Availability & Support",
        body: "RelayRigs is currently in a pre-launch phase and does not guarantee a specific uptime service level at this stage. RelayRigs uses commercially reasonable efforts to keep the Platform available and provides support via support@relayrigs.com.",
      },
      {
        heading: "5. What Happens When Something Breaks",
        body: "If the Platform experiences an outage or defect materially affecting your use, RelayRigs will use commercially reasonable efforts to investigate and restore service, and will communicate planned maintenance in advance where practicable. RelayRigs is not liable for delays or failures caused by third-party services it depends on (including the RelayRigs mobile app backend, payment processors, or ELD providers).",
      },
      {
        heading: "6. Confidentiality",
        body: "Each party will keep the other party's non-public business information confidential and use it only as needed to perform under this MSA.",
      },
      {
        heading: "7. Warranties & Disclaimers",
        body: "During this pre-launch period, the Platform is provided \"as is\" and \"as available,\" without warranties of any kind, express or implied, including any warranty of merchantability, fitness for a particular purpose, or non-infringement.",
      },
      {
        heading: "8. Limitation of Liability",
        body: "RelayRigs' total liability under this MSA will not exceed the total fees you paid RelayRigs in the twelve (12) months preceding the claim, consistent with the Limitation of Liability clause in our Terms of Service.",
      },
      {
        heading: "9. Termination",
        body: "Either party may terminate this MSA for material breach that is not cured within 30 days of written notice. RelayRigs may also suspend or terminate access for violations of the Terms of Service or Acceptable Use Policy.",
      },
      {
        heading: "10. Force Majeure",
        body: "Neither party is liable for any delay or failure to perform resulting from causes beyond its reasonable control, including natural disasters, internet or telecommunications failures, or third-party service outages.",
      },
      {
        heading: "11. Governing Law & Dispute Resolution",
        body: "This MSA is governed by the laws of the State of Florida. Any dispute arising under this MSA will be brought exclusively in the state or federal courts located in Florida.",
      },
      {
        heading: "12. Entire Agreement",
        body: "This MSA, together with the Terms of Service, Privacy Policy, and any applicable order form, constitutes the entire agreement between you and RelayRigs regarding the Services and supersedes any prior agreements on the same subject.",
      },
    ],
  },

  "fmcsa-compliance-overview": {
    title: "FMCSA Compliance & Functionality Overview",
    lastUpdated: "August 2026",
    sections: [
      {
        heading: "Important Note",
        body: "RelayRigs is a workflow, documentation, and coordination platform. It assists drivers and motor carriers and verifies that configured workflow steps have been satisfied. RelayRigs does not make legal determinations, is not \"FMCSA approved\" or \"DOT certified,\" and does not guarantee that a driver or vehicle will pass a roadside inspection. The responsible motor carrier and other legally responsible parties remain responsible for the determinations assigned to them by applicable law.",
      },
      {
        heading: "1. Purpose & Guiding Principles",
        body: [
          "RelayRigs connects stakeholders — responsible motor carriers, owner-operators, and relief drivers — to keep an interstate or intrastate shipment moving safely, with a documentation trail centered on core FMCSA safety questions: is the driver ready, is the vehicle ready, are the documents ready, is the ELD ready, are hours-of-service ready, are there unresolved defects, and is the assignment ready to begin.",
          "Carrier responsibility is preserved — RelayRigs does not transfer or replace the responsible motor carrier's legal obligations.",
          "Configurable, not conclusive — RelayRigs verifies that configured workflow steps were completed; it does not decide legality or make a compliance determination on the carrier's behalf.",
          "Safety is made visible — defects, expirations, and readiness gaps are surfaced clearly so they can be resolved before a relief leg begins.",
        ],
      },
      {
        heading: "2. The Responsible Motor Carrier Model",
        body: [
          "FMCSA guidance places accountability for a shipment on a single responsible motor carrier, even when the driver behind the wheel changes. RelayRigs models this directly.",
          "Model A — Own Authority: an owner-operator moves the load under their own motor carrier authority (Motor Carrier Self Authorization).",
          "Model B — Leased to a Carrier: the load moves under another carrier's authority, and that responsible carrier must authorize the relief driver before they can take over.",
          "Other configurable relationship types include Owner-Operator Own Authority and Leased Owner-Operator, matching how FMCSA classifies carrier relationships.",
        ],
      },
      {
        heading: "3. The Relief Driver Authorization Pipeline",
        body: [
          "Before a relief driver is authorized to take over a leg, RelayRigs walks the assignment through a 17-step pipeline rather than a simple request-and-accept model:",
          "1. Relief Request Created", "2. Responsible Motor Carrier Identified", "3. Motor Carrier Authority / Relationship Review",
          "4. Qualified Driver Matched", "5. Driver Interest / Conditional Acceptance", "6. Carrier / Employer Authorization",
          "7. Driver Qualification Clearance", "8. Clearinghouse / Drug & Alcohol Clearance", "9. Insurance Clearance",
          "10. ELD Driver Authorization / Connection", "11. Hours-of-Service Clearance", "12. Daily / Pre-Trip Inspection",
          "13. Payment / Fuel Readiness", "14. Handoff In Progress", "15. Verified Custody Handoff",
          "16. Final Pre-Trip Readiness", "17. Driver Takes Control → Relief Leg Active",
        ],
      },
      {
        heading: "4. How the Compliance Gates Work",
        body: [
          "RelayRigs uses three classes of configurable gates to control progression through the workflow:",
          "Hard Block — must be completed before operation. A driver who hasn't cleared a hard block sees a \"Compliance Hold — Driver Not Authorized to Begin Relief Leg\" status.",
          "Warning — flags issues such as stale data for manual review, without necessarily stopping the workflow outright.",
          "Info — contextual, record-keeping information that supports the compliance documentation trail.",
          "Gates are configurable by jurisdiction, assignment type, vehicle type, carrier, driver relationship, cargo, hazmat status, and interstate/intrastate operation.",
          "Every verification records its evidence source — connected ELD provider, motor carrier attestation, driver-submitted document, or authorized administrator review. RelayRigs never fabricates a verification and never converts uncertain data into a compliant/non-compliant conclusion on its own.",
        ],
      },
      {
        heading: "5. Driver Qualification, ELD & Hours of Service",
        body: [
          "RelayRigs distinguishes between \"CDL verified\" and \"legally authorized to drive this load\" — the latter also depends on driver-qualification, Clearinghouse/drug-and-alcohol, insurance, ELD, and hours-of-service gates. The responsible employer/carrier remains responsible for the determinations assigned to it by law.",
          "ELD: the platform reads connection and duty status from the connected ELD provider and will surface statuses like \"ELD Disconnected\" or \"ELD Data Stale\" rather than guessing.",
          "Hours of Service: driving time, the 14-hour window, required breaks, and cycle data are reviewed from ELD data; clearly insufficient hours trigger a hard block.",
          "Driver credentials tracked include CDL class, medical status, endorsements, and expiration dates.",
        ],
      },
      {
        heading: "6. Daily Inspection Readiness (Pre-Trip)",
        body: [
          "A structured pre-trip checklist is modeled on CVSA Level I, II, and III inspection categories: driver & documents, ELD & HOS, tractor, engine/leaks, brakes & air, steering & suspension, tires/wheels/hubs, lights, coupling, trailer, cargo & securement, and a 360° walk-around.",
          "Readiness is shown as Green, Yellow, Orange, Red, or Gray.",
          "A Green result is never labeled \"DOT certified,\" \"DOT approved,\" or \"guaranteed to pass.\" It only means no blocking condition was identified through the RelayRigs checklist — it is not a government inspection.",
          "A Do-Not-Operate defect places the vehicle on a safety hold and blocks the assignment until resolved.",
        ],
      },
      {
        heading: "7. Verified Custody Handoff & Audit Trail",
        body: [
          "Truck, trailer, bill of lading, seal, and cargo condition are verified and recorded at each custody handoff, creating a traceable chain of custody.",
          "RelayRigs keeps an immutable audit trail of who did what, when, and from which source — covering inspections, defects, safety holds, repairs, authorizations, signatures, and reviews.",
        ],
      },
      {
        heading: "8. Roadside Inspection Mode & Data Boundaries",
        body: [
          "A read-only Roadside Inspection Mode presents organized, authorized information for a roadside encounter: driver identity, CDL status/class/endorsements, medical status, carrier and USDOT number, truck/trailer details, registration and periodic inspection info, current HOS summary, ELD provider and connection status, active assignment, shipping documents, hazmat status, the previous handoff, and today's pre-trip record.",
          "This view does not replace the connected ELD provider's required roadside data-transfer workflow, and it excludes sensitive data outside the scope of a roadside encounter.",
        ],
      },
      {
        heading: "9. What RelayRigs Does — and Does Not — Do",
        body: [
          "RelayRigs DOES: verify that configured workflow steps were completed; keep the responsible motor carrier identified and accountable; separate \"accepted\" from \"authorized to operate\"; record evidence, attestations, signatures, and an audit trail; and surface defects, safety holds, ELD/HOS issues, and expirations.",
          "RelayRigs DOES NOT: make legal determinations or claim to be FMCSA/DOT approved; guarantee a driver or vehicle will pass a roadside inspection; sell, rent, or transfer operating authority or a USDOT number; present estimated data as verified ELD/HOS data; or help hide defects, falsify records, or circumvent enforcement.",
        ],
      },
      {
        heading: "10. Configurable Compliance References",
        body: "RelayRigs treats FMCSA and CVSA guidance as external, configurable references (with jurisdiction, authority, title, URL, effective date, last-verified date, and status) rather than hardcoding them — allowing references to be updated as regulations change and to vary by jurisdiction, vehicle, cargo, hazmat status, and assignment type, with support for additional jurisdictions over time.",
      },
      {
        heading: "11. Legal Notice",
        body: "This document is provided for informational purposes only and is not legal advice. It describes how the RelayRigs application is designed to operate and how its workflow supports documentation and coordination consistent with FMCSA safety concepts. RelayRigs does not make legal determinations and is not affiliated with, endorsed by, or approved by FMCSA, CVSA, or the U.S. Department of Transportation. Motor carriers, owner-operators, drivers, and other responsible parties remain responsible for compliance with all applicable laws and regulations and should consult qualified transportation counsel regarding their specific circumstances. FMCSA and CVSA regulations and guidance are subject to change; verify current requirements with the applicable authority.",
      },
    ],
  },
};
